import React, { useEffect, useState } from 'react';
import {
  Box,
  Toolbar,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Button,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ReceiptLongOutlined, DeleteForeverOutlined } from '@mui/icons-material';
import { API } from '../../../utils/Api';
import axios from 'axios';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Images from '../../../utils/Images';

const StockRequestDetails = () => {
  const token = localStorage.getItem('token');
  const location = useLocation();
  const navigate = useNavigate();

  const [indent, setIndent] = useState(location.state?.indent || null);
  const [loading] = useState(!location.state?.indent);
  const [receivedInputs, setReceivedInputs] = useState({});
  const [receiving, setReceiving] = useState(false);
  const [confirmReceiveOpen, setConfirmReceiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';

    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDisplayStatus = (status = '') => {
    const s = status.toUpperCase();
    if (s === 'COMPLETED') return 'DISPATCHED';
    return s;
  };

  const getStatusStyle = (status = '') => {
    const s = status.toUpperCase();

    if (s === 'PENDING') return { bg: '#fff7e6', color: '#b26a00', border: '#ffd591' };
    if (s === 'APPROVED') return { bg: '#e8f4ff', color: '#0b5cab', border: '#91caff' };
    if (s === 'COMPLETED') return { bg: '#e9f7ef', color: '#1e7e34', border: '#95d5b2' };
    if (s === 'RECEIVED') return { bg: '#e6fffb', color: '#00796b', border: '#87e8de' };
    if (s === 'CLOSED') return { bg: '#e9f7ef', color: '#1e7e34', border: '#95d5b2' };
    if (s === 'REJECTED' || s === 'CANCELLED') {
      return { bg: '#fff1f0', color: '#c62828', border: '#ffa39e' };
    }

    return { bg: '#f4f6f8', color: '#34495e', border: '#d9dee3' };
  };

  const getReceivedQty = (item) => item.totalReceivedQuantity ?? 0;

  const handleBack = () => navigate(-1);

  const handleEditIndent = () => {
    navigate('/stockRequest', {
      state: {
        mode: 'edit',
        indent,
      },
    });
  };

  const handleOpenReceiveConfirm = () => {
    setConfirmReceiveOpen(true);
  };

  const handleCloseReceiveConfirm = () => {
    if (!receiving) {
      setConfirmReceiveOpen(false);
    }
  };

  const handleCloseConfirmDialog = () => {
    if (!receiving) {
      setConfirmCloseOpen(false);
    }
  };

  const handleReceivedChange = (id, value) => {
    setReceivedInputs((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleMarkAsReceived = async () => {
    try {
      setReceiving(true);

      const payloadItems = indent.items.map((it) => ({
        rawMaterial: it.rawMaterial?._id || it.rawMaterial,
        receivedQuantity: Number(receivedInputs[it._id] || 0),
      }));

      await axios.put(
        `${API.BASE_URL}stock-requisition/update-recieved-quantity`,
        {
          _id: indent._id,
          items: payloadItems,
          status: 'RECEIVED',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setIndent((prev) => ({
        ...prev,
        status: 'RECEIVED',
        items: prev.items.map((it) => ({
          ...it,
          receivedQuantity: Number(receivedInputs[it._id] || 0),
          transferredQuantity: Number(receivedInputs[it._id] || 0),
        })),
      }));

      toast.success('Received quantities saved');
      navigate('/stock-requistion-requests');
      setConfirmReceiveOpen(false);
    } catch (error) {
      console.error('Failed to save received quantity:', error);
      toast.error(
        error?.response?.data?.error ||
        'Failed to save received quantities.'
      );
    } finally {
      setReceiving(false);
    }
  };

  const handleCloseIndent = async () => {
    try {
      setReceiving(true);

      await axios.put(
        `${API.BASE_URL}stock-requisition/update-status-closed`,
        {
          _id: indent._id,
          status: 'CLOSED',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setIndent((prev) => ({
        ...prev,
        status: 'CLOSED',
      }));
      navigate('/stock-requistion-requests');
      toast.success('Indent closed successfully.');
    } catch (error) {
      console.error('Failed to close indent:', error);
      toast.error(
        error?.response?.data?.error ||
        'Failed to close indent.'
      );
    } finally {
      setReceiving(false);
    }
  };

  const handleDeleteStockRequisition = async () => {
    if (!indent?._id) return;

    try {
      setDeleteLoading(true);

      await axios.delete(`${API.BASE_URL}stock-requisition/delete`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          _id: indent._id,
        },
      });

      toast.success('Stock requisition deleted successfully.');
      setDeleteOpen(false);
      navigate(-1);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to delete stock requisition.'
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    if (indent?.items) {
      const initial = {};
      indent.items.forEach((it) => {
        initial[it._id] = '';
      });
      setReceivedInputs(initial);
    }
  }, [indent]);

  if (loading || !indent) {
    return (
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f3f4f7 0%, #eef2f7 100%)',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, pb: 4 }}>
          <Box
            sx={{
              height: '60vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <CircularProgress size={32} />
            <Typography sx={{ mt: 1.8, fontSize: 14 }}>
              Loading indent details...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const items = indent.items || [];
  const upperStatus = (indent.status || 'PENDING').toUpperCase();

  const canEdit = upperStatus === 'PENDING';
  const canReceive = upperStatus === 'COMPLETED' || upperStatus === 'RECEIVED';
  const canClose = upperStatus === 'RECEIVED';
  const showReceivedQty = upperStatus === 'CLOSED';
  const canDownload =
    upperStatus === 'ACCEPTED' ||
    upperStatus === 'APPROVED' ||
    upperStatus === 'COMPLETED' ||
    upperStatus === 'RECEIVED' ||
    upperStatus === 'CLOSED';

  const statusStyle = getStatusStyle(upperStatus);

  const handleDownloadPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [9, 165, 247];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 24, 'F');

    try {
      doc.addImage(Images.indentImg, 'PNG', 10, 0, 50, 20);
    } catch (e) { }

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('INVOICE', pageWidth - 14, 10, { align: 'right' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Stock Indent', pageWidth - 14, 16, { align: 'right' });

    doc.setTextColor(40, 40, 40);

    const startY = 34;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Indent:', 14, startY);
    doc.setFont('helvetica', 'bold');
    doc.text(indent.indentNumber || '-', 35, startY);

    doc.setFont('helvetica', 'normal');
    doc.text('Status:', 14, startY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(getDisplayStatus(upperStatus), 35, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.text('Date:', 110, startY);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(indent.createdAt), 130, startY);

    doc.setFont('helvetica', 'normal');
    doc.text('FY:', 110, startY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(indent.fin_year || '-', 130, startY + 6);

    let totalRequested = 0;
    let totalReceived = 0;

    const tableRows = items.map((it, index) => {
      const rm = it.rawMaterial || {};
      const requested = Number(it.requestedQuantity || 0);
      const received = Number(getReceivedQty(it) || 0);

      totalRequested += requested;
      totalReceived += received;

      return [
        index + 1,
        rm.name || '-',
        rm.unit || '-',
        requested,
        showReceivedQty ? received : '-',
      ];
    });

    autoTable(doc, {
      startY: 50,
      head: [['#', 'Item', 'Unit', 'Req Qty', 'Rec Qty']],
      body: tableRows,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [245, 250, 255],
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'right' },
        4: { halign: 'right' },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');

    doc.text(
      `Items: ${items.length}   |   Requested: ${totalRequested}   |   Received: ${showReceivedQty ? totalReceived : '-'
      }`,
      pageWidth - 14,
      finalY,
      { align: 'right' }
    );

    doc.setFont('helvetica', 'normal');
    doc.text('Authorized Signature', pageWidth - 60, finalY + 18);

    doc.save(`Invoice-${indent.indentNumber || 'Details'}.pdf`);
  };

  const TopDownloadButton = () => (
    <>
      {canDownload && (
        <Button
          variant="contained"
          startIcon={<ReceiptLongOutlined />}
          onClick={handleDownloadPDF}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 2.5,
            px: 2.2,
            bgcolor: '#6a1b9a',
            color: '#fff',
            '&:hover': {
              bgcolor: '#4a148c',
            },
          }}
        >
          Download PDF
        </Button>
      )}
    </>
  );

  const BottomActionButtons = () => (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ width: { xs: '100%', sm: 'auto' } }}
    >
      {canEdit && (
        <Button
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={handleEditIndent}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 2.5,
            px: 2.2,
            bgcolor: '#003049',
            color: '#fff',
            '&:hover': {
              bgcolor: '#001d2e',
            },
          }}
        >
          Edit Indent
        </Button>
      )}

      {canReceive && (
        <Button
          variant="contained"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={handleOpenReceiveConfirm}
          disabled={receiving}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 2.5,
            px: 2.2,
            bgcolor: '#2e7d32',
            color: '#fff',
            '&:hover': {
              bgcolor: '#1b5e20',
            },
          }}
        >
          Save Received Qty
        </Button>
      )}

      {canClose && (
        <Button
          variant="contained"
          onClick={() => setConfirmCloseOpen(true)}
          disabled={receiving}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 2.5,
            px: 2.2,
            bgcolor: '#003049',
            color: '#fff',
            '&:hover': {
              bgcolor: '#001d2e',
            },
          }}
        >
          Close Indent
        </Button>
      )}

      {canEdit && (
        <Button
          variant="contained"
          startIcon={<DeleteForeverOutlined />}
          onClick={() => setDeleteOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 2.5,
            px: 2.2,
            bgcolor: '#d32f2f',
            color: '#fff',
            '&:hover': {
              bgcolor: '#b71c1c',
            },
          }}
        >
          Delete
        </Button>
      )}
    </Stack>
  );

  const InfoCard = ({ icon, label, value }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        height: '100%',
        border: '1px solid rgba(0,48,73,0.08)',
        bgcolor: '#ffffff',
      }}
    >
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(15,76,117,0.08)',
            color: '#0f4c75',
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 14.5, fontWeight: 900, color: '#17202a' }}>
            {value || '-'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  console.log('upperStatus', upperStatus);

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f3f4f7 0%, #eef2f7 100%)',
        minHeight: '100vh',
      }}
    >
      <Toolbar />

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, pb: 5 }}>
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            mb: 2.5,
            p: { xs: 2.2, md: 3 },
            borderRadius: 4,
            background: `
              radial-gradient(circle at top right, rgba(255,255,255,0.20), transparent 34%),
              linear-gradient(135deg, #0f4c75 0%, #003049 100%)
            `,
            color: '#fff',
            boxShadow: '0 18px 45px rgba(0, 48, 73, 0.22)',
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.6} alignItems="center">
                <IconButton
                  onClick={handleBack}
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.13)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
                  }}
                >
                  <ArrowBackRoundedIcon />
                </IconButton>

                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.18)',
                  }}
                >
                  <Inventory2OutlinedIcon sx={{ fontSize: 30 }} />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: { xs: 21, md: 21, lg: 25 }, fontWeight: 900 }}>
                    Indent Details
                  </Typography>

                  <Typography sx={{ fontSize: { xs: 9, md: 11, lg: 13.5 }, opacity: 0.86, mt: 0.3 }}>
                    Review requested stock items and update receiving status
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                <Chip
                  label={getDisplayStatus(upperStatus)}
                  sx={{
                    height: 36,
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: 0.6,
                    bgcolor: statusStyle.bg,
                    color: statusStyle.color,
                    border: `1px solid ${statusStyle.border}`,
                  }}
                />

                <TopDownloadButton />
              </Stack>
            </Stack>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<NumbersOutlinedIcon fontSize="small" />}
                  label="Indent Number"
                  value={indent.indentNumber}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<CalendarMonthOutlinedIcon fontSize="small" />}
                  label="Requested On"
                  value={formatDate(indent.createdAt)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<CalendarMonthOutlinedIcon fontSize="small" />}
                  label="Financial Year"
                  value={indent.fin_year}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<ReceiptLongOutlined fontSize="small" />}
                  label={`Item${items.length > 1 ? 's' : ''}`}
                  value={`${items.length} item${items.length > 1 ? 's' : ''}`}
                />
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={(theme) => ({
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            border: `1px solid ${theme.palette.divider}`,
            background: '#fff',
            boxShadow: '0 14px 35px rgba(15, 23, 42, 0.08)',
          })}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 2.4 }}
          >
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: '#17202a' }}>
                Requested Items
              </Typography>

              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.3 }}>
                Items included in this stock requisition
              </Typography>
            </Box>

            <Chip
              label={`${items.length} item${items.length > 1 ? 's' : ''}`}
              sx={{
                fontWeight: 900,
                bgcolor: 'rgba(15,76,117,0.08)',
                color: '#0f4c75',
              }}
            />
          </Stack>

          {items.length === 0 ? (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                borderRadius: 3,
                bgcolor: 'rgba(15,76,117,0.035)',
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
              <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 700 }}>
                No items found for this indent.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.4}>
              {items
                .sort((a, b) => {
                  const nameA = (a.rawMaterial?.name || '').toLowerCase();
                  const nameB = (b.rawMaterial?.name || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((it, index) => {
                  const rm = it.rawMaterial || {};
                  const rmName = rm.name || '-';
                  const unit = rm.unit || '';
                  const qty = it.requestedQuantity ?? '-';
                  const receivedQty = getReceivedQty(it);
                  const shouldShowInput =
                    (upperStatus === 'COMPLETED' || upperStatus === 'RECEIVED') 
                    && (it.balanceQuantity === null || Number(it.balanceQuantity) > 0);


                  return (
                    <Paper
                      key={it._id || index}
                      elevation={0}
                      sx={{
                        p: { xs: 1.7, md: 2 },
                        borderRadius: 3,
                        border: '1px solid rgba(15,76,117,0.08)',
                        background:
                          'linear-gradient(135deg, rgba(15,76,117,0.045), rgba(255,255,255,0.95))',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1.5}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              minWidth: 36,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#003049',
                              color: '#fff',
                              fontSize: 13,
                              fontWeight: 900,
                            }}
                          >
                            {index + 1}
                          </Box>

                          <Box>
                            <Typography sx={{ fontSize: 16, fontWeight: 900, color: '#17202a' }}>
                              {rmName}
                            </Typography>

                            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.2 }}>
                              Unit: {unit || '-'}
                            </Typography>
                          </Box>

                          <Chip
                            label={`Req: ${qty} ${unit}`}
                            sx={{
                              fontWeight: 900,
                              bgcolor: '#fff7e6',
                              color: '#b26a00',
                              border: '1px solid #ffd591',
                            }}
                          />
                          <Chip
                            label={`Tot. Rcvd: ${receivedQty} ${unit}`}
                            sx={{
                              fontWeight: 900,
                              bgcolor: '#e9f7ef',
                              color: '#1e7e34',
                              border: '1px solid #95d5b2',
                            }}
                          />
                        </Stack>


                        {shouldShowInput ? (
                          <Box sx={{ minWidth: 140 }}>
                            <Box
                              sx={{
                                minWidth: 170,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: '#ffffff',
                                border: '1px solid #b7e4c7',
                                borderRadius: 3,
                                px: 1.2,
                                py: 0.6,
                                boxShadow: '0 6px 16px rgba(30,126,52,0.10)',
                              }}
                            >
                              <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#1e7e34' }}>
                                Received
                              </Typography>

                              <TextField
                                size="small"
                                type="text"
                                value={receivedInputs[it._id] || ''}
                                onChange={(e) => handleReceivedChange(it._id, e.target.value)}
                                placeholder="Qty"
                                inputProps={{ min: 0 }}
                                sx={{
                                  width: 82,
                                  '& .MuiOutlinedInput-root': {
                                    height: 38,
                                    borderRadius: 2,
                                    bgcolor: '#f7fff9',
                                    fontWeight: 900,
                                    color: '#1e7e34',
                                  },
                                  '& input': {
                                    textAlign: 'center',
                                    p: '7px 8px',
                                    fontSize: 14,
                                    fontWeight: 900,
                                  },
                                }}
                              />

                              <Typography sx={{ fontSize: 11.5, fontWeight: 900, color: '#6b7280' }}>
                                {unit || ''}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (upperStatus === 'CLOSED' && (
                          <Chip
                            label={`Received: ${receivedQty} ${unit}`}
                            sx={{
                              fontWeight: 900,
                              bgcolor: '#e9f7ef',
                              color: '#1e7e34',
                              border: '1px solid #95d5b2',
                            }}
                          />
                        )
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
            </Stack>
          )}

          {(canEdit || canReceive || canClose) && (
            <>
              <Divider sx={{ my: 3 }} />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
              >
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {canReceive
                    ? 'You can save received quantities multiple times until the indent is closed.'
                    : canClose
                      ? 'Close this indent after final verification.'
                      : 'You can still edit this indent because it is pending.'}
                </Typography>

                <BottomActionButtons />
              </Stack>
            </>
          )}
        </Paper>
      </Box>

      <Dialog
        open={confirmReceiveOpen}
        onClose={handleCloseReceiveConfirm}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 2.5,
            background: 'linear-gradient(135deg, #0f4c75 0%, #003049 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon />
            </Box>

            <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
              Confirm Action
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: '#34495e',
              lineHeight: 1.6,
              marginTop: 1,
            }}
          >
            Are you sure you want to save received quantities for this indent?
            <br />
            <br />
            You can still edit received quantities until the indent is{' '}
            <Box component="span" sx={{ fontWeight: 900, color: '#1e7e34' }}>
              CLOSED
            </Box>.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={handleCloseReceiveConfirm}
            disabled={receiving}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              color: '#34495e',
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleMarkAsReceived}
            disabled={receiving}
            startIcon={
              receiving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              borderRadius: 2,
              bgcolor: '#2e7d32',
              '&:hover': {
                bgcolor: '#1b5e20',
              },
            }}
          >
            {receiving ? 'Saving...' : 'Yes, Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteLoading && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 2.5,
            background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningAmberRoundedIcon />
            <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
              Delete Requisition
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: 14, color: '#34495e', lineHeight: 1.6 }}>
            Are you sure you want to delete this stock requisition?
            <br />
            <br />
            This action <b>cannot be undone</b>.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleteLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              color: '#34495e',
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleDeleteStockRequisition}
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteForeverOutlined />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: '#d32f2f',
              '&:hover': {
                bgcolor: '#b71c1c',
              },
            }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCloseConfirmDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 2.5,
            background: 'linear-gradient(135deg, #003049 0%, #001d2e 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon />
            </Box>

            <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
              Close Indent
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: '#34495e',
              lineHeight: 1.6,
              mt: 1,
            }}
          >
            Are you sure you want to close this indent?
            <br /><br />
            Once closed, <b>received quantities cannot be edited</b>.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={handleCloseConfirmDialog}
            disabled={receiving}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              color: '#34495e',
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={async () => {
              await handleCloseIndent();
              setConfirmCloseOpen(false);
            }}
            disabled={receiving}
            startIcon={
              receiving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: '#003049',
              '&:hover': {
                bgcolor: '#001d2e',
              },
            }}
          >
            {receiving ? 'Closing...' : 'Yes, Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockRequestDetails;